import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { parseDataRodadaIso } from 'helper/Relatorios/Dessem';
import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { useGetDessemOptionsQuery } from 'services/api/products/relatorios';

/** Reaproveita o cache do `GET options` (o da hidratação, em geral) se tiver menos de 60 s. */
export const OPTIONS_REFETCH_APOS_S = 60;
/** Reconsulta a cada 5 min com o painel montado: o Core recebe estudos ao longo do dia. */
export const OPTIONS_POLLING_MS = 5 * 60 * 1000;

/**
 * Último dia selecionável no calendário do Informe DESSEM, como `Date` (meia-noite local):
 * `options.max_data_rodada`, já como Data do Informe (admin = D do estudo mais recente da Ampere
 * no Core; cliente = último dia publicado). Com um backend sem o campo cai em
 * `default_data_rodada` e, na falta (erro, carregando, data inválida), na data padrão do perfil:
 * admin hoje, cliente ontem (o mesmo critério da hidratação) — nunca lança.
 *
 * Compartilha o cache do options com o `useDessemHidratacao`; a hidratação não se repete
 * (o slice tem a trava `hidratado`, e aquele hook `skip`a a consulta depois dela). O polling
 * só existe enquanto o `DessemControlPanel` está montado. Memoizado pelos dois ISOs, não pelo
 * objeto `data`.
 */
export default function useDessemMaxData(): Date {
  const { data } = useGetDessemOptionsQuery(undefined, {
    refetchOnMountOrArgChange: OPTIONS_REFETCH_APOS_S,
    pollingInterval: OPTIONS_POLLING_MS,
  });
  const isAdmin = Boolean(useSelector(selectUserIsAdmin));
  const maxIso = data?.max_data_rodada;
  const defaultIso = data?.default_data_rodada;

  return useMemo(() => {
    const doOptions = parseDataRodadaIso(maxIso) ?? parseDataRodadaIso(defaultIso);
    if (doOptions) return doOptions;
    const padrao = new Date();
    if (!isAdmin) padrao.setDate(padrao.getDate() - 1);
    return padrao;
  }, [maxIso, defaultIso, isAdmin]);
}
